/**
 * TalentReviewResetAdmin — start the Annual I&G Review again on a test environment (Victor, 15 Sep 2026).
 *
 * UAT testers rehearse the review from scheduling to completion, then start over. This does from
 * /admin what scripts/reset_talent_demo.py does from a workstation, through the same server code:
 * it removes the sessions with their seats, placement lists and decisions, removes what completing
 * them published, keeps a copy of what it removed and puts the demo's talent baseline back. The
 * server allows it only for a signed-in admin or HR admin, and never in production.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { talentReviewService } from '@/services/api/talentReviewService'
import type { TalentReviewResetResult } from '@/services/api/talentReviewService'
import { useConfirm } from '@/hooks/useConfirm'

const EVERY_CHAIR = '__every_chair__'

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`

const refusal = (error: unknown, action: string): string => {
  switch ((error as { status?: number } | undefined)?.status) {
    case 401:
      return 'Your sign-in has expired. Sign in again and retry.'
    case 403:
      return 'Only admin and HR admin can reset the Annual I&G Review.'
    case 404:
      return 'Resetting the Annual I&G Review is not available in this environment.'
    case 503:
      return 'Your sign-in could not be checked just now. Try again in a moment.'
    default:
      return `The Annual I&G Review could not be ${action}. Try again in a moment.`
  }
}

const statusLabel = (status: string) => {
  const words = status.replace(/_/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-ZA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Africa/Johannesburg',
      })
    : 'No date set'

export default function TalentReviewResetAdmin() {
  const [chair, setChair] = useState<string>(EVERY_CHAIR)
  const [result, setResult] = useState<{ at: Date; data: TalentReviewResetResult } | null>(null)
  const { confirm } = useConfirm()
  const queryClient = useQueryClient()
  const facilitatorId = chair === EVERY_CHAIR ? undefined : chair

  const {
    data: preview,
    error,
    isPlaceholderData,
  } = useQuery({
    queryKey: ['talent', 'demo-reset', chair],
    queryFn: () => talentReviewService.getDemoResetPreview(facilitatorId),
    // Keep the chair list on screen while another chair's sessions load.
    placeholderData: (previous) => previous,
    retry: false,
  })
  const chairs = preview?.chairs ?? []
  const sessions = preview?.sessions ?? []
  const published = preview?.published
  const chairName = (id?: string | null) => (id ? chairs.find((c) => c.id === id)?.name || id : 'every chair')

  const reset = useMutation({
    mutationFn: (id?: string) => talentReviewService.resetDemo(id),
    onSuccess: (data) => {
      setResult({ at: new Date(), data })
      setChair(EVERY_CHAIR)
      // The hub, the grid and the scheduling lists all read talent queries.
      queryClient.invalidateQueries({ queryKey: ['talent'] })
      toast.success('The Annual I&G Review has been reset')
    },
    onError: (err) => toast.error(refusal(err, 'reset')),
  })

  const startReset = async () => {
    if (!preview) return
    const invites = sessions.filter((s) => s.calendar_invite).length
    const proceed = await confirm(
      `This removes ${plural(sessions.length, 'session')} for ${chairName(facilitatorId)}, with their seats, ` +
        `placement lists and decisions, and what completed sessions published for ${preview.review_year}. ` +
        "The demo's talent baseline is put back and a copy of what is removed is kept." +
        (invites ? ` ${plural(invites, 'calendar invite')} already sent will stay in Outlook until cancelled there.` : ''),
      'Reset the Annual I&G Review?',
      { confirmText: 'Reset review', type: 'danger' },
    )
    if (proceed) reset.mutate(facilitatorId)
  }

  const publishedLine = published
    ? [
        plural(published.placements, 'placement'),
        plural(published.high_potentials, 'high-potential'),
        plural(published.key_persons, 'key person'),
        plural(published.succession_plans, 'succession plan'),
        plural(published.growth_plan_items, 'growth-plan item'),
      ].join(', ')
    : ''

  const done = result?.data
  const doneInvites = done ? done.sessions.filter((s) => s.calendar_invite).length : 0

  return (
    <div className="space-y-4" data-demo="admin-talent-reset">
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-heritage-blue/10 p-2">
            <RotateCcw className="h-5 w-5 text-heritage-blue" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold">Annual I&amp;G Review</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Start the review again from scheduling. Resetting removes the sessions with their seats, placement lists
              and decisions, and what completed sessions published. The demo's talent baseline is put back and a copy
              of what is removed is kept.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="talent-reset-chair">Whose sessions</Label>
            <Select value={chair} onValueChange={setChair} disabled={reset.isPending}>
              <SelectTrigger id="talent-reset-chair">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={EVERY_CHAIR}>Every chair</SelectItem>
                {chairs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name || c.id} · {plural(c.sessions, 'session')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="destructive"
            onClick={startReset}
            disabled={!preview || isPlaceholderData || !!error || reset.isPending}
            data-demo="admin-talent-reset-run"
          >
            {reset.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
            {reset.isPending ? 'Resetting…' : 'Reset review'}
          </Button>
        </div>

        {error ? (
          <p className="mt-4 text-sm text-red-600">{refusal(error, 'checked')}</p>
        ) : !preview ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking the sessions…
          </p>
        ) : (
          <div className="mt-4 space-y-3" data-demo="admin-talent-reset-preview">
            {sessions.length === 0 ? (
              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                No sessions{facilitatorId ? ` for ${chairName(facilitatorId)}` : ''}. Resetting still removes anything
                published for {preview.review_year} and puts the baseline back.
              </p>
            ) : (
              <ul className="divide-y rounded-md border">
                {sessions.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-start justify-between gap-2 p-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium">{s.name || 'Unnamed session'}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.chair_name || s.chair_id || 'No chair'} · {when(s.scheduled_date)} ·{' '}
                        {plural(s.leaders, 'People Leader')} · {plural(s.placements, 'placement')} ·{' '}
                        {plural(s.decisions, 'decision')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {s.calendar_invite && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                          <CalendarClock className="h-3.5 w-3.5" /> Invite sent
                        </span>
                      )}
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{statusLabel(s.status)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-muted-foreground">
              Also removed, as published for {preview.review_year}: {publishedLine}.
              {preview.baseline_rows > 0 ? ` The baseline puts back ${plural(preview.baseline_rows, 'row')}.` : ''}
            </p>
          </div>
        )}
      </Card>

      {result && done && (
        <Card className="p-5" data-demo="admin-talent-reset-result">
          <div className="flex flex-wrap items-center gap-2">
            {done.starting_point.restored_from ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            )}
            <h3 className="font-semibold">
              Reset for {done.chair_id ? done.sessions[0]?.chair_name || done.chair_id : 'every chair'}
            </h3>
            <span className="text-xs text-muted-foreground">
              {result.at.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'Sessions removed', value: done.sessions.length },
              { label: 'Placements removed', value: done.published.placements },
              { label: 'High-potentials and key persons removed', value: done.published.high_potentials + done.published.key_persons },
              {
                label: done.starting_point.restored_from === 'dev_seeds' ? 'Rows seeded again' : 'Baseline rows put back',
                value: done.starting_point.rows,
              },
            ].map((c) => (
              <div key={c.label} className="rounded-md border p-3">
                <dt className="text-xs text-muted-foreground">{c.label}</dt>
                <dd className="text-xl font-semibold">{c.value}</dd>
              </div>
            ))}
          </dl>
          <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
            {!done.starting_point.restored_from && (
              <li className="text-amber-800">No talent baseline is stored on this environment, so nothing was put back.</li>
            )}
            {done.backup_prefix && <li>A copy of what was removed is in the performance.{done.backup_prefix}* tables.</li>}
            {doneInvites > 0 && <li>{plural(doneInvites, 'calendar invite')} already sent stay in Outlook until cancelled there.</li>}
          </ul>
        </Card>
      )}
    </div>
  )
}
